import React, { useCallback, useContext, useMemo, useState } from 'react';
import { ButtonV2 } from '@withjoy/joykit';
import type { ButtonV2Props } from '@withjoy/joykit';
import { PeopleContext } from '@shared/context/people';
import { usePeoplePickerButtonController } from './usePeoplePickerButtonController';
import type { HardcodedLabel } from './usePeoplePickerButtonController';
import { PeopleListItem } from '@shared/components/PeopleListItem';
import { useTranslation } from '@shared/core';
import type { PeoplePickerUseCase } from './PeoplePicker.types';
import { usePreloadTranslationNamespaces } from '@shared/hooks/usePreloadTranslationNamespaces';

export type PeoplePickerItemType = Readonly<'simple' | 'withDeliveryMethod'>;

export type PeoplePickerButtonProps = Readonly<
  Omit<ButtonV2Props, 'onClick' | 'children' | 'title' | 'itemType'> & {
    buttonTextDefault?: string;
    buttonTextHover?: string;
    eventHandle: string;
    title?: React.ReactNode;
    hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
    onStartSelection?: () => void;
    useCase: PeoplePickerUseCase;
  } & ({ itemType?: 'simple'; enableSmsSelection?: never; smsOnly?: never } | { itemType: 'withDeliveryMethod'; enableSmsSelection: boolean; smsOnly?: boolean })
>;

export const PeoplePickerButton: React.FC<PeoplePickerButtonProps> = ({
  eventHandle,
  title: _title,
  hardcodedLabels,
  enableSmsSelection = false,
  smsOnly = false,
  itemType = 'simple',
  onStartSelection,
  useCase,

  buttonTextDefault: _buttonTextDefault,
  buttonTextHover,

  intent = 'primary',
  variant = 'outline',
  disabled,
  ...buttonProps
}) => {
  const { tq } = useTranslation('peoplePickerButton');

  usePreloadTranslationNamespaces('peoplePicker');

  const { selectedPeople, setSelectedPeople } = useContext(PeopleContext);

  const title = _title || tq`Select Guests`;
  const buttonTextDefault = _buttonTextDefault || tq`Select Guests`;

  const [isHovering, setIsHovering] = useState(false);

  const personItemRender = useCallback(
    person => {
      if (itemType === 'withDeliveryMethod') {
        return (
          <PeopleListItem.WithDeliveryMethod
            enableSmsSelection={enableSmsSelection}
            {...person}
          />
        );
      }

      return <PeopleListItem.Simple {...person} />;
    },
    [itemType, enableSmsSelection],
  );

  const { loading, startSelection, error } = usePeoplePickerButtonController({
    eventHandle,
    title,
    onSelectedPeople: setSelectedPeople,
    enableSmsSelection,
    smsOnly,
    hardcodedLabels,
    personItemRender,
    useCase,
  });

  const handleClick = () => {
    onStartSelection?.();
    startSelection();
  };

  const buttonText = useMemo(
    () => (isHovering && selectedPeople?.length && buttonTextHover ? buttonTextHover : buttonTextDefault),
    [isHovering, buttonTextHover, buttonTextDefault, selectedPeople],
  );

  return (
    <ButtonV2
      {...buttonProps}
      intent={intent}
      variant={variant}
      disabled={disabled || !!error}
      loading={loading}
      onClick={handleClick}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      borderRadius="999px"
    >
      {buttonText}
    </ButtonV2>
  );
};
